import React, { PropsWithChildren } from 'react'
import {
  ComponentConfig,
  ComponentData,
  Slot,
  SlotComponent,
  usePuck,
} from '@puckeditor/core'
import ReferralProgressBar from 'steps/referral/components/ReferralProgressBar'
import { RichtextField } from 'breaktime-theme/src/puck/fields/RichtextField'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import {
  BorderOverlayProps,
  BorderStyleProps,
  ShadowProps,
  borderOverlayField,
  borderOverlayStyle,
  borderStyleField,
  shadowStyleField,
  applyBorderStyle,
  applyShadowStyle,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import type { ReferralButtonProps } from '../ReferralButton/ReferralButton'
import { PqHideResultsField, PqHideConfig } from './PqHideResultsField'

/**
 * Components that may NOT be dropped inside a referral card's free content
 * slot (step containers + step-internal blocks that only make sense in their
 * own tree), mirroring LISTICLE_DISALLOW.
 */
export const REFERRAL_DISALLOW = [
  'ReferralContainer',
  'ReferralCard',
  'ReferralButton',
  'ShopButton',
  'ReferralDropdown',
  'ListicleContainer',
  'ListicleItem',
  'ListicleVoting',
  'PollContainer',
  'PollQuestion',
  'PollAnswer',
  'PollProduct',
  'PollProductPanel',
  'PollNextQuestionButton',
]

/** Rich text (tiptap) sidebar field storing an HTML string. */
export const richtextField = (label: string) => ({
  type: 'custom' as const,
  label,
  render: ({
    value,
    onChange,
    id,
  }: {
    value?: string
    onChange: (v: string) => void
    id?: string
  }) => (
    <RichtextField
      field={{ type: 'custom', label }}
      value={value}
      onChange={onChange}
      id={id}
    />
  ),
})

export type ReferralCardProps = {
  id?: string
  topText?: string
  bottomText?: string
  shadow?: ShadowProps
  border?: BorderStyleProps
  borderOverlay?: BorderOverlayProps
  /** Card background — empty falls back to the theme card color
   * (--card-background-color); supports rgba for transparent cards. */
  cardBackground?: string
  mediaBackground?: string
  mediaBackgroundHover?: string
  /** PQ hide-list (AC: PQ dynamically updates referral): chosen source step
   * + result refs. Maps to the legacy `dynamics` the referral runtime
   * filters on. */
  pqHideResults?: PqHideConfig
  /** Media area — legacy card image position; any media block (image, video…). */
  media: Slot
  /** Optional media shown in place of `media` while the card is hovered
   * (cross-fade, the legacy custom-css ::before/::after trick). */
  mediaHover: Slot
  /** Action area — ReferralButton or ReferralDropdown. */
  action: Slot
  /** Free slot for extra blocks inside the card (below the description). */
  content: Slot
}

export const REFERRAL_CARD_DEFAULT_PROPS: ReferralCardProps = {
  topText: '<b>Product name</b>',
  bottomText: 'Short product description',
  media: [],
  mediaHover: [],
  action: [],
  content: [],
}

const stripOuterParagraph = (html: string) => {
  const m = html.trim().match(/^<p>([\s\S]*)<\/p>$/)
  // only unwrap a SINGLE paragraph — multi-paragraph content keeps its <p>s
  return m && !m[1].includes('<p') ? m[1] : html.trim()
}

/**
 * Normalize tiptap output for the legacy card markup: an "empty" field emits
 * `<p></p>` (truthy → phantom block), and a single wrapping <p> adds
 * paragraph margins legacy beaker content (bare `<b>text</b>` spans) never
 * had. Returns undefined when there is no visible content.
 */
export const cleanRichtext = (v?: string): string | undefined => {
  if (!v) return undefined
  const html = stripOuterParagraph(v)
  const hasText = html.replace(/<[^>]*>/g, '').trim().length > 0
  const hasMedia = /<(img|video|iframe)/i.test(html)
  return hasText || hasMedia ? html : undefined
}


/** CSS custom props consumed by referral-puck.scss — media area background,
 * plus its card-hover variant (hover needs a stylesheet, not inline styles). */
export const referralCardVars = (p: {
  mediaBackground?: string
  mediaBackgroundHover?: string
}): React.CSSProperties =>
  ({
    ...(p.mediaBackground
      ? { '--referral-media-bg': p.mediaBackground }
      : {}),
    ...(p.mediaBackgroundHover
      ? { '--referral-media-hover-bg': p.mediaBackgroundHover }
      : {}),
  } as React.CSSProperties)

/** Full inline style for the card root — shared by the editor render and the
 * production mapping (`link.puckCardStyle`). */
export const referralCardStyle = (
  p: Pick<
    ReferralCardProps,
    'shadow' | 'border' | 'cardBackground' | 'mediaBackground' | 'mediaBackgroundHover'
  >,
): React.CSSProperties => ({
  // empty = transparent: overrides the legacy var(--card-background-color)
  // default so clearing the picker actually clears the card (poll/listicle
  // puck cards default transparent the same way)
  backgroundColor: p.cardBackground || 'transparent',
  ...(borderOverlayStyle((p as ReferralCardProps).borderOverlay)
    ? { position: 'relative' }
    : {}),
  ...applyBorderStyle(p.border ?? {}),
  ...applyShadowStyle(p.shadow),
  ...referralCardVars(p),
})

/** First ShopButton (or legacy ReferralButton) in the action slot (card
 * media links to its url). */
export const getCardAction = (
  action?: ComponentData[],
): { type: string; props: ReferralButtonProps } | undefined =>
  (action as { type: string; props: ReferralButtonProps }[] | undefined)?.find(
    a => a.type === 'ShopButton' || a.type === 'ReferralButton',
  )

/**
 * One referral card — legacy BUTTON-type card markup from
 * ReferralCardNoVoting.jsx (`.referral-card.button-card.card-true`), styled by
 * steps/referral/ReferralStep.scss (imported by ReferralContainer).
 */
export const PuckReferralCard: ComponentConfig<{
  props: ReferralCardProps
  fields: ThemeCustomFields
}> = {
  label: 'Referral Card',
  defaultProps: REFERRAL_CARD_DEFAULT_PROPS,
  fields: {
    topText: richtextField('Top Text'),
    bottomText: richtextField('Bottom Text'),
    shadow: shadowStyleField,
    border: borderStyleField,
    borderOverlay: borderOverlayField,
    cardBackground: {
      type: 'colorPicker',
      label: 'Card Background',
      clearable: true,
      alpha: true,
    },
    mediaBackground: {
      type: 'colorPicker',
      label: 'Media Background',
      clearable: true,
    },
    mediaBackgroundHover: {
      type: 'colorPicker',
      label: 'Media Background (card hover)',
      clearable: true,
    },
    pqHideResults: {
      type: 'custom',
      label: 'Hide for Personality Quiz results',
      render: ({ value, onChange }: any) => (
        <PqHideResultsField value={value} onChange={onChange} />
      ),
    },
    media: { type: 'slot', label: 'Media', disallow: REFERRAL_DISALLOW },
    mediaHover: {
      type: 'slot',
      label: 'Media on Hover',
      disallow: REFERRAL_DISALLOW,
    },
    action: {
      type: 'slot',
      label: 'Action',
      allow: ['ShopButton', 'ReferralButton', 'ReferralDropdown'],
    },
    content: {
      type: 'slot',
      label: 'Extra Content',
      disallow: REFERRAL_DISALLOW,
    },
  },
  render: props => <ReferralCardRender {...props} />,
}

type ReferralCardRenderProps = Omit<
  ReferralCardProps,
  'media' | 'mediaHover' | 'action' | 'content'
> & {
  media: SlotComponent
  mediaHover: SlotComponent
  action: SlotComponent
  content: SlotComponent
}

const slotLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'rgba(0,0,0,0.4)',
  marginBottom: 4,
}

const slotHintStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  color: 'rgba(0,0,0,0.35)',
  pointerEvents: 'none',
  zIndex: 1,
}

/** Labeled slot with a centered drop hint while the slot is empty. */
const SlotSection = ({
  label,
  hint,
  isEmpty,
  children,
}: PropsWithChildren<{ label: string; hint: string; isEmpty: boolean }>) => (
  <div style={{ marginTop: 8 }}>
    <span style={slotLabelStyle}>{label}</span>
    <div style={{ position: 'relative' }}>
      {isEmpty && <span style={slotHintStyle}>{hint}</span>}
      {children}
    </div>
  </div>
)

function ReferralCardRender(props: ReferralCardRenderProps) {
  const {
    id,
    media: Media,
    mediaHover: MediaHover,
    action: Action,
    content: Content,
  } = props
  const topText = cleanRichtext(props.topText)
  const bottomText = cleanRichtext(props.bottomText)

  // Slot emptiness for the drop hints — read this card's stored props from the
  // container's items (same appState lookup ListicleItem uses for numbering).
  const puck = usePuck()
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const container = content.find(c => c.type === 'ReferralContainer')
  const stored = (
    (container?.props?.items ?? []) as ComponentData[]
  ).find(it => it.props?.id === id)
  const isSlotEmpty = (name: 'media' | 'mediaHover' | 'action' | 'content') =>
    !((stored?.props?.[name] as ComponentData[] | undefined)?.length)

  // Mirror the legacy card flavors: a ReferralDropdown in the action slot
  // makes this a MENU card (`.dropdown-card` + `.dropdown-inside` wrapper);
  // ONE_COLUMN cards are text-only (legacy hides the card image there).
  const storedAction = (stored?.props?.action ?? []) as ComponentData[]
  const isDropdownCard = storedAction.some(a => a.type === 'ReferralDropdown')
  const containerProps = container?.props as
    | {
        layout?: string
        voting?: {
          mode?: string
          goal?: number
          barColor?: string
          barHighlightColor?: string
          barCompleteColor?: string
        }
      }
    | undefined
  const layout = containerProps?.layout
  const votingConfig = containerProps?.voting
  const votingOn = !!votingConfig?.mode && votingConfig.mode !== 'NONE'
  const showMedia = true
  // ONE_COLUMN legacy scss lays the card + .referral-bottom out as centered
  // flex rows — fine for the flat production markup, but it squeezes the
  // editor's labeled slot sections side-by-side. Force block flow in the
  // editor for that layout (production markup doesn't have these sections).
  const editorFlowFix: React.CSSProperties =
    layout === 'ONE_COLUMN' ? { display: 'block', width: '100%' } : {}

  const inner = (
    <>
      {showMedia && (
        <>
          <SlotSection
            label="Media"
            hint="Drop an Image / Video here"
            isEmpty={isSlotEmpty('media')}
          >
            <div
              className={isDropdownCard ? 'referral-image-dropdown' : 'referral-image'}
            >
              <Media disallow={REFERRAL_DISALLOW} minEmptyHeight={120} />
            </div>
          </SlotSection>
          {/* rendered side-by-side in the editor (stacking would block the
              media slot's drop targets); production overlays it with a
              cross-fade on card hover (referral-puck.scss) */}
          <SlotSection
            label="Media on Hover (optional)"
            hint="Drop replacement media — cross-fades on card hover"
            isEmpty={isSlotEmpty('mediaHover')}
          >
            <MediaHover disallow={REFERRAL_DISALLOW} minEmptyHeight={24} />
          </SlotSection>
        </>
      )}
      <div className="referral-bottom" style={editorFlowFix}>
        {(topText || bottomText) && (
          <div className="description">
            {topText && (
              <span
                style={{ display: 'block' }}
                dangerouslySetInnerHTML={{ __html: topText }}
              ></span>
            )}
            {bottomText && (
              <span
                style={{ display: 'block' }}
                dangerouslySetInnerHTML={{ __html: bottomText }}
              ></span>
            )}
          </div>
        )}
        <SlotSection
          label="Extra Content (optional)"
          hint="Drop any component here"
          isEmpty={isSlotEmpty('content')}
        >
          <Content disallow={REFERRAL_DISALLOW} minEmptyHeight={24} />
        </SlotSection>
        {votingOn && (
          <div style={{ padding: '4px 0' }}>
            <ReferralProgressBar
              currentVotes={Math.round((votingConfig?.goal || 100) * 0.4)}
              totalVotes={votingConfig?.goal || 100}
              highlight={false}
              barColor={votingConfig?.barColor}
              completeColor={votingConfig?.barCompleteColor}
              highlightColor={votingConfig?.barHighlightColor}
              showVoteCount={votingConfig?.mode === 'PER_ITEM'}
            />
            <span style={{ ...slotLabelStyle, marginTop: 2 }}>
              Voting preview — live tallies on the published break
            </span>
          </div>
        )}
        <SlotSection
          label="Action"
          hint="Drop a Shop Button / Referral Dropdown here"
          isEmpty={isSlotEmpty('action')}
        >
          <Action
            allow={['ShopButton', 'ReferralButton', 'ReferralDropdown']}
            minEmptyHeight={48}
          />
        </SlotSection>
      </div>
    </>
  )

  return (
    // width:100% fills the grid column the container's items slot creates —
    // puck wraps each slot child in a div, so the legacy .card-list flex
    // sizing can't reach the card in the editor.
    <div
      className={`referral-card ${
        isDropdownCard ? 'dropdown-card' : 'button-card card-true'
      } puck-referral-card`}
      style={{
        width: '100%',
        ...editorFlowFix,
        ...referralCardStyle(props),
      }}
    >
      {borderOverlayStyle(props.borderOverlay) && (
        <div aria-hidden style={borderOverlayStyle(props.borderOverlay)} />
      )}
      {isDropdownCard ? (
        // legacy scss paints .dropdown-inside (root is background: initial
        // !important), so the card style must land here for MENU cards
        <div className="dropdown-inside" style={referralCardStyle(props)}>
          {inner}
        </div>
      ) : (
        inner
      )}
    </div>
  )
}
